import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GARMIN_ENABLED } from '../config/features';
import { acceptGarminAiConsent, consumeGarminReturn, disconnectGarmin, fetchGarminStatus, startGarminConnect,
  withdrawGarminAiConsent } from '../utils/garmin';
import { TRAINING_SOURCES } from '../config/trainingSources';
import { ManageSheet, MoreButton, ProviderLogo, ProviderName, StatusPill } from './connections/ConnectionParts';

// Garmin pilot (2026-10-09): connect · status · disconnect. Renders nothing unless the frontend flag is
// on AND the backend reports this user as an eligible pilot user. Since 2026-10-11 it renders as a
// compact row inside Profile's "חיבורי אימונים" (TrainingConnectionsSection); logic unchanged.
const RETURN_HINT = {
  success: 'החיבור התקבל — ממתינים לאישור מ-Garmin…',
  denied: 'החיבור בוטל. אפשר לנסות שוב בכל עת.',
  error: 'החיבור לא הושלם. נסה שוב.',
};
// Consent texts — v3 since 2026-10-27 (the AI clause names OpenAI and Google Gemini; v2 named Gemini only). Shown
// before connecting (both required) and, for a link made under v1 or v2, as a one-step upgrade of the AI clause.
// The server validates the version + AI tick and stores it.
export const GARMIN_DATA_CONSENT =
  'אני מאשר/ת ש-RitmoIQ תקבל באמצעות Stridee את פעילויות הריצה שלי מ-Garmin Connect, כולל הקובץ המקורי ' +
  '(שעשוי לכלול מיקום). הקובץ המקורי נשמר מוצפן, מוגדר להימחק אוטומטית לאחר 14 יום, ונמחק מיד אם אנתק את Garmin.';
export const GARMIN_AI_CONSENT =
  'אני מאשר/ת ש-RitmoIQ תשלח נתוני אימון מהפעילויות האלה (כגון תאריך, מרחק, זמן, קצב, דופק והקפות) לשירותי ' +
  'בינה מלאכותית חיצוניים, OpenAI ו-Google Gemini, לצורך ניתוח אישי של האימונים, המלצות והתאמת תוכנית ' +
  'האימונים שלי. ' +
  'מיקום/GPS, הקובץ המקורי ומזהי המכשיר אינם נשלחים. RitmoIQ אינה משתמשת בנתונים שלי כדי לאמן מודלי AI משלה.';
const POLL_MS = 5000;
const POLL_MAX = 24;          // ~2 minutes after a return or while syncing

// "סנכרון אחרון" (Profile V2): the latest completed activity's processing time from /garmin/status
// (last_completed_at). Shown only for a valid timestamp; never invented.
export function lastSyncText(iso, now = new Date()) {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime()) || t.getTime() > now.getTime() + 60000) return null;
  const pad = (x) => String(x).padStart(2, '0');
  const hm = `${pad(t.getHours())}:${pad(t.getMinutes())}`;
  const sameDay = t.toDateString() === now.toDateString();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (sameDay) return `היום ${hm}`;
  if (t.toDateString() === y.toDateString()) return `אתמול ${hm}`;
  return `${pad(t.getDate())}/${pad(t.getMonth() + 1)} ${hm}`;
}

export default function GarminConnectCard({ onStatus } = {}) {
  const [info, setInfo] = useState(undefined);      // undefined = loading, null = hidden
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const [aiConsent, setAiConsent] = useState(false);
  const [upgradeTick, setUpgradeTick] = useState(false);
  const [hint, setHint] = useState(null);
  const [managing, setManaging] = useState(false);   // manage sheet (disconnect lives there)
  const [connectOpen, setConnectOpen] = useState(false);  // consent + connect, after "חיבור"
  const polls = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const s = await fetchGarminStatus();
      setInfo(s && s.eligible ? s : null);
      return s;
    } catch {
      setInfo((prev) => (prev === undefined ? null : prev));
      return null;
    }
  }, []);

  useEffect(() => {
    if (!GARMIN_ENABLED) return undefined;
    const ret = consumeGarminReturn();
    if (ret) setHint(RETURN_HINT[ret]);
    refresh();
    const t = setInterval(async () => {
      polls.current += 1;
      if (polls.current > POLL_MAX) { clearInterval(t); return; }
      const s = await refresh();
      if (!s || !s.eligible) { clearInterval(t); return; }
      if (s && s.status === 'connected' && !s.syncing && ret !== 'success') clearInterval(t);
      if (s && s.status === 'connected') setHint(null);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  // Profile header pill (Profile V2): report what this row already knows; null = row hidden.
  useEffect(() => { if (onStatus) onStatus(info || null); }, [info, onStatus]);

  if (!GARMIN_ENABLED || !info) return null;

  const connect = async () => {
    setBusy(true);
    try { await startGarminConnect(); } catch { setHint(RETURN_HINT.error); setBusy(false); }
  };
  const upgrade = async () => {
    setBusy(true);
    try { await acceptGarminAiConsent(); setUpgradeTick(false); setHint('ההסכמה עודכנה. החיבור ל-Garmin נשאר כמו שהיה.'); await refresh(); }
    catch { setHint('עדכון ההסכמה נכשל. נסה שוב.'); }
    setBusy(false);
  };
  // Withdraw the AI clause (2026-10-12): v2 -> v1 on the same link. No disconnect, no data / file deletion, no
  // re-import; the server stops AI use of Garmin data immediately. Renewing = the explicit consent block below.
  const withdrawAi = async () => {
    if (!window.confirm('לבטל את ההסכמה לניתוח AI? נתוני Garmin לא יישלחו יותר לניתוח AI. החיבור ל-Garmin והפעילויות נשארים.')) return;
    setBusy(true);
    try { await withdrawGarminAiConsent(); setManaging(false); setHint('ההסכמה לניתוח AI בוטלה. החיבור ל-Garmin נשאר פעיל.'); await refresh(); }
    catch { setHint('ביטול ההסכמה נכשל. נסה שוב.'); }
    setBusy(false);
  };
  const disconnect = async () => {
    if (!window.confirm('לנתק את Garmin? הסנכרון ייפסק והקבצים המקוריים יימחקו.')) return;
    setBusy(true);
    try { await disconnectGarmin(); setHint(null); setManaging(false); await refresh(); } catch { setHint('הניתוק נכשל. נסה שוב.'); }
    setBusy(false);
  };

  const source = TRAINING_SOURCES.find((x) => x.id === 'garmin');
  const connected = info.status === 'connected';
  const needsReauth = info.status === 'reauth_required';
  const rowStatus = needsReauth || (connected && info.error) ? 'error' : connected ? 'connected' : 'disconnected';
  const statusLabel = needsReauth ? 'נדרש חיבור מחדש'
    : connected && info.error ? 'שגיאה בסנכרון'
    : connected && info.syncing ? 'מחובר · מסנכרן'
    : info.status === 'pending' ? 'ממתין לאישור' : undefined;
  // v1 link (no AI clause) or v2 link (AI clause naming Google Gemini only): the server says an upgrade is available;
  // never inferred client-side. Until the user accepts it, Garmin data never reaches OpenAI (server-side).
  const needsAiConsent = (connected || needsReauth) && info.consent_upgrade_available === true;
  const renewingAi = info.ai_consent === true;          // v2: the existing AI consent is being updated, not added
  const n = Number(info.completed_activities || 0);
  const countText = n === 1 ? 'פעילות אחת התקבלה' : `${n} פעילויות התקבלו`;
  const lastSync = lastSyncText(info.last_completed_at);

  // Row inside Profile's "חיבורי אימונים" (2026-10-11). Same status / connect / consent / return /
  // disconnect logic as before; disconnect lives in the manage sheet ("⋯").
  return (
    <div className="px-4 py-3.5" dir="rtl" data-testid="garmin-row">
      <div className="flex items-center gap-3">
        <ProviderLogo source={source} size={44} />
        <div className="min-w-0 flex-1">
          <ProviderName source={source} />
          <div className="text-[14px] text-text-muted">{source.description}</div>
        </div>
        {connected ? (
          <>
            <StatusPill status={rowStatus} label={statusLabel} />
            <MoreButton label="ניהול חיבור Garmin" onClick={() => setManaging(true)} />
          </>
        ) : (
          <>
            {statusLabel && <StatusPill status={rowStatus} label={statusLabel} />}
            {!connectOpen && (
              <button type="button" onClick={() => setConnectOpen(true)}
                      className="h-11 px-4 rounded-control border border-accent text-accent font-semibold text-[15px] shrink-0">
                {needsReauth ? 'חיבור מחדש' : 'חיבור'}
              </button>
            )}
          </>
        )}
      </div>
      {connected && n > 0 && (
        <p className="mt-2.5 rounded-control bg-surface-2 px-3 py-2 text-[13px] text-text-muted" data-testid="garmin-count">
          {lastSync && <span data-testid="garmin-last-sync">סנכרון אחרון: <bdi dir="ltr">{lastSync}</bdi> · </span>}
          {countText} · מוצגות ב"האימונים שלי"
        </p>
      )}
      {info.error && connected && (
        <p className="mt-2 text-[13px] text-quality">הייתה בעיה בעיבוד הפעילות האחרונה מ-Garmin.</p>
      )}
      {hint && <p className="mt-2 text-[13px] text-text-body">{hint}</p>}
      {needsAiConsent && (
        <div className="mt-3 space-y-3 rounded-control bg-surface-2 px-3 py-3" data-testid="garmin-ai-consent">
          <p className="text-[14px] font-semibold text-text-body">
            {renewingAi ? 'עדכון ההסכמה לניתוח האימונים שלך ממאמן ה-AI' : 'הסכמה לניתוח האימונים שלך ממאמן ה-AI'}
          </p>
          {renewingAi && (
            <p className="text-[13px] text-text-muted">
              מאמן ה-AI עובד כעת גם עם OpenAI. עד לאישור, נתוני Garmin נשלחים רק ל-Google Gemini, כפי שאישרת.
            </p>
          )}
          <p className="text-[13px] text-text-muted">החיבור ל-Garmin נשאר כמו שהוא: בלי ניתוק, בלי מחיקת קבצים ובלי יבוא מחדש.</p>
          <label className="flex items-start gap-2 text-[13px] text-text-body leading-relaxed">
            <input type="checkbox" className="mt-1" checked={upgradeTick} onChange={(e) => setUpgradeTick(e.target.checked)} />
            <span>{GARMIN_AI_CONSENT} פרטים במדיניות הפרטיות, סעיף 9.</span>
          </label>
          <button type="button" onClick={upgrade} disabled={busy || !upgradeTick}
                  className="w-full h-11 rounded-control btn-primary text-[15px]">
            אישור ההסכמה המעודכנת
          </button>
        </div>
      )}
      {!connected && connectOpen && (
        <div className="mt-3 space-y-3">
          <label className="flex items-start gap-2 text-[13px] text-text-body leading-relaxed">
            <input type="checkbox" className="mt-1" aria-label="הסכמה לקבלת נתוני Garmin" checked={consent}
                   onChange={(e) => setConsent(e.target.checked)} />
            <span>{GARMIN_DATA_CONSENT}</span>
          </label>
          <label className="flex items-start gap-2 text-[13px] text-text-body leading-relaxed">
            <input type="checkbox" className="mt-1" aria-label="הסכמה לניתוח AI" checked={aiConsent}
                   onChange={(e) => setAiConsent(e.target.checked)} />
            <span>{GARMIN_AI_CONSENT} פרטים במדיניות הפרטיות, סעיף 9.</span>
          </label>
          <button type="button" onClick={connect} disabled={busy || !consent || !aiConsent}
                  className="w-full h-12 rounded-control btn-primary text-[15px]">
            {needsReauth ? 'חבר מחדש את Garmin' : 'חבר Garmin'}
          </button>
        </div>
      )}
      {managing && (
        <ManageSheet
          source={source}
          status={rowStatus}
          statusLabel={statusLabel}
          rows={[['שלב', source.badge || '—'], ['פעילויות שהתקבלו', String(n)], ['מה מסונכרן', 'פעילויות ריצה'],
                 ...(typeof info.ai_consent === 'boolean' ? [['ניתוח AI', info.ai_consent ? 'פעיל' : 'כבוי']] : [])]}
          secondaryAction={info.ai_consent === true ? { label: 'ביטול ההסכמה לניתוח AI', onClick: withdrawAi } : null}
          note={'הפעילויות מוצגות ב"האימונים שלי".'}
          onClose={() => setManaging(false)}
          onDisconnect={disconnect}
          disconnectLabel="ניתוק Garmin"
          busy={busy}
        />
      )}
    </div>
  );
}
